import { useDropzone } from 'react-dropzone';
// @mui
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
// type
import { UploadProps } from './type';
//
import NextImage from '../NextImage';
import RejectionFiles from './RejectionFiles';
import BlockContent from './BlockContent';
import { isAudioType, isImageType, isVideoType } from '../../utils/contentType';
import { height } from '@mui/system';

// ----------------------------------------------------------------------

const DropZoneStyle = styled('div')(({ theme }) => ({
  outline: 'none',
  overflow: 'hidden',
  position: 'relative',
  padding: theme.spacing(5, 1),
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create('padding'),
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  '&:hover': { opacity: 0.72, cursor: 'pointer' },
}));

// ----------------------------------------------------------------------

export default function UploadSingleFile({
  error = false,
  file,
  helperText,
  sx,
  customBlockContent,
  audioSizeLimit=false,
  applySizeLimit=true,
  ...other
}: UploadProps) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple: false,
    ...other,
  });

  const fileType = typeof file === 'string' ? file : file?.type;

  const videoFormat = [
    'video/mp4', // MP4
    'video/webm', // WebM
    'video/ogg', // OGG
    'video/x-msvideo', // AVI
    // 'video/quicktime',
    // 'video/mov',
  ];

  const photoFormat = [
    'image/png', // PNG
    'image/jpeg', // JPEG
    'image/jpg', // JPG
    'image/gif', // GIF
    'image/bmp', // BMP
    'image/svg+xml', // SVG
    'image/tiff', // TIFF
  ];

  const audioFormat = [
    'audio/mp3',
    'audio/mpeg',
    'audio/wav'
  ]

  let isVideo, isPhoto, isMov,isAudio;
  console.log(isVideo);
  console.log(fileType);
  if (fileType) {
    isVideo = videoFormat.includes(fileType);
    isPhoto = photoFormat.includes(fileType);
    isAudio = audioFormat.includes(fileType);
    isMov = fileType === 'video/quicktime';
  } else {
    isPhoto = isImageType(file as string);
    isVideo = isVideoType(file as string);
    isAudio = isAudioType(file as string);
  }

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <DropZoneStyle
        {...getRootProps()}
        sx={{
          bgcolor: 'black',
          ...(isDragActive && { opacity: 0.72 }),
          ...((isDragReject || error) && {
            color: 'error.main',
            borderColor: 'error.light',
            bgcolor: 'error.lighter',
          }),
          ...(file && {
            padding: '12% 0',
          }),
        }}
      >
        <input {...getInputProps()} />

        {!file && (customBlockContent || <BlockContent applySizeLimit={applySizeLimit} audioSizeLimit={audioSizeLimit}/>)}

        {file && (
          <Box p={5}>
            {isVideo && (
              <video
                key={typeof file === 'string' ? file : (file.preview as string)}
                style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  width: 'calc(100% - 16px)',
                  height: 'calc(100% - 16px)',
                  objectFit: 'contain',
                  borderRadius: 8,
                }}
                muted
              >
                <source
                  src={typeof file === 'string' ? file : (file.preview as string)}
                  type="video/mp4"
                />
                <source
                  src={typeof file === 'string' ? file : (file.preview as string)}
                  type="video/quicktime"
                />
                Your browser does not support this format
              </video>
            )}

            {isPhoto && (
              <NextImage
                alt="file preview"
                src={typeof file === 'string' ? file : (file.preview as string)}
                sx={{
                  top: 8,
                  left: 8,
                  borderRadius: 1,
                  position: 'absolute',
                  width: 'calc(100% -  50px)',
                  height: 'calc(100% - 16px)',
                }}
                fill={true}
              />
            )}

            {isMov && (
              <NextImage
                alt="file preview"
                src={'/assets/DefaultVideoPreview.svg'}
                sx={{
                  top: 'calc(50% - 50px)',
                  left: 'calc(50% - 100px)',
                  borderRadius: 1,
                  position: 'absolute',
                  height: 'auto',
                }}
                width={200}
                height={200}
              />
            )}

            {isAudio && (
              <NextImage
                alt="file preview"
                src={'/assets/AudioUploading.svg'}
                sx={{
                  top: 'calc(50% - 50px)',
                  left: 'calc(50% - 50px)',
                  borderRadius: 1,
                  position: 'absolute',
                }}
                width={100}
                height={100}
              />
            )}
          </Box>
        )}
      </DropZoneStyle>

      {fileRejections.length > 0 && <RejectionFiles fileRejections={[...fileRejections]} />}

      {helperText}
    </Box>
  );
}
